<template>
  <main class="pt-10 md:mx-14 mx-4">
    <div class="flex items-center gap-x-4 lg:gap-x-12">
      <div
        @click="goBack"
        class="flex items-center border-main rounded-lg shadow-xs w-20 gap-x-2 text-gray-90 border-gray-90 px-1 py-1.5 lg:py-2 font-semibold text-fg-default text-xs cursor-pointer justify-center"
      >
        <i class="el-icon-back"></i>
        <p class="font-light text-xs">Back</p>
      </div>
      <div class="flex items-center gap-x-2 lg:gap-x-4">
        <bulk-payout-icon-2 />
        <span class="text-lg font-light"></span>
        <p class="font-normal text-xs lg:text-sm">Select Beneficiaries</p>
      </div>
    </div>

    <div
      class="mt-8 flex flex-col lg:flex-row gap-8 lg:gap-0 lg:justify-between"
    >
      <div class="w-full lg:w-40% h-full p-4 border border-gray-120 rounded-xl">
        <h1 class="text-black-10 text-base font-bold">
          Select Beneficiaries / Add New
        </h1>
        <button
          class="mt-6 py-4 w-full flex items-center justify-center gap-3 rounded-lg text-primary bg-secondary-2 hover:opacity-80 font-normal text-sm"
          @click="openAddBeneficiary"
        >
          <plus-icon />
          Add New Beneficiary
        </button>

        <p class="mt-12 text-sm font-semibold text-black-10">
          Select from Saved Beneficiaries
        </p>
        <el-input class="w-full my-4" v-model="search" placeholder="Search">
          <i
            slot="prefix"
            class="inline-flex items-center el-input__icon el-icon-search text-xl text-black-22 pl-1"
          ></i>
        </el-input>

        <div class="my-5" v-if="isLoading">
          <spinner />
        </div>

        <div
          v-if="!isLoading && filteredBeneficiaries.length > 0"
          class="mt-2 flex flex-col gap-4"
        >
          <transition-group name="fade" tag="div" class="space-y-4">
            <div
              v-for="item in filteredBeneficiaries"
              :key="item.id"
              class="flex items-center border cursor-pointer border-gray-120 hover:border-gray-300 rounded-lg p-5 bg-gray-30"
              @click="showDialog(item)"
            >
              <div class="w-1/6">
                <p
                  class="h-12 w-12 flex items-center justify-center rounded-full bg-secondary-2 text-primary uppercase font-bold text-base"
                >
                  {{ getNameInitials(item.details.bankDetails.accountName) }}
                </p>
              </div>

              <div class="w-5/6 flex items-center justify-between">
                <div>
                  <h5
                    class="font-bold text-black-10 text-base sm:text-lg capitalize"
                  >
                    {{
                      formatAccountName(item.details.bankDetails.accountName)
                    }}
                  </h5>
                  <p
                    class="text-gray-muted text-xs sm:text-sm font-normal mt-1 capitalize"
                  >
                    {{ item.details.bankDetails.bankName || '--' }} |
                    {{ item.details.bankDetails.accountNumber || '--' }}
                  </p>
                </div>
                <arrow-icon size="h-7 w-7" color="#BEC0D1" />
              </div>
            </div>
          </transition-group>
        </div>
      </div>

      <div
        class="w-full lg:w-58% h-full py-4 px-5 border border-gray-120 rounded-xl"
      >
        <h4 class="text-sm font-bold text-black-10 flex items-center gap-3">
          Review and Confirm Beneficiaries
          <p
            v-if="selectedBeneficiaries.length > 0"
            class="text-primary font-medium text-sm bg-secondary-2 h-6 w-8 flex items-center justify-center rounded-full"
          >
            {{ selectedBeneficiaries.length }}
          </p>
        </h4>

        <div v-if="selectedBeneficiaries.length > 0" class="mt-3">
          <div class="flex flex-col">
            <div
              v-for="item in selectedBeneficiaries"
              :key="item.id"
              class="review-items"
            >
              <div class="w-2/3 flex items-center gap-5">
                <div
                  class="h-12 w-12 flex items-center justify-center rounded-full bg-secondary-2 text-primary uppercase font-bold text-base"
                >
                  {{ getNameInitials(item.details.bankDetails.accountName) }}
                </div>
                <div>
                  <h5
                    class="font-bold text-black-10 text-base sm:text-lg capitalize"
                  >
                    {{
                      formatAccountName(item.details.bankDetails.accountName)
                    }}
                  </h5>
                  <p
                    class="text-gray-muted text-xs sm:text-sm font-normal mt-1 capitalize"
                  >
                    {{ item.details.bankDetails.bankName || '--' }} |
                    {{ item.details.bankDetails.accountNumber || '--' }}
                  </p>
                </div>
              </div>
              <div
                class="w-1/3 flex justify-end text-right items-center gap-10 pr-4"
              >
                <div>
                  <h5 class="font-bold text-black-10 text-base sm:text-lg">
                    {{ formattedCurrency }} {{ parseAmount(item.amount) }}
                  </h5>
                  <p class="text-gray-25 text-xs font-normal mt-1">Amount:</p>
                </div>
                <span
                  @click="openReviewOptions(item.id)"
                  class="cursor-pointer"
                >
                  <more-icon />
                </span>
              </div>

              <ul
                v-if="showReviewOptions && item.id === selectedId"
                class="absolute top-20 -right-2 z-10 bg-white rounded-lg border border-gray-120 list-none py-3 px-5 font-normal text-sm"
              >
                <li @click="openEditBeneficiary(item)" class="list-item">
                  <edit-icon />
                  Edit Details
                </li>
                <li @click="openRemoveBeneficiary" class="list-item">
                  <remove-icon />
                  Remove Beneficiary
                </li>
              </ul>
            </div>

            <div
              v-if="showReviewOptions"
              @click="closeReviewOptions"
              class="fixed top-0 left-0 h-screen w-full"
            ></div>
          </div>

          <button
            class="mt-10 mb-3 w-full py-4 rounded-lg text-white bg-primary outline-none hover:opacity-80"
            @click="confirmBeneficiaries"
          >
            Confirm {{ selectedBeneficiaries.length }} Beneficiaries
          </button>
        </div>

        <div v-else class="flex flex-col items-center text-center my-20">
          <empty-icon />
          <h4 class="text-black-10 font-bold text-lg mt-5">
            No Beneficiaries Selected Yet
          </h4>
          <span class="text-sm font-normal text-gray-muted mt-1"
            >Your Beneficiaries will show up here once you select them.</span
          >
        </div>
      </div>
    </div>

    <!-- drawers -->
    <add-beneficiary
      v-if="showAddBeneficiary"
      :showDrawer="showAddBeneficiary"
      @closeDrawer="closeAddBeneficiary"
    />
    <edit-beneficiary
      v-if="showEditBeneficiary"
      :showDrawer="showEditBeneficiary"
      :details="details"
      @closeDrawer="closeEditBeneficiary"
    />

    <!-- dialogs -->
    <enter-amount
      v-if="showAmountDialog"
      :showDialog="showAmountDialog"
      :details="details"
      @closeDialog="closeDialog"
    />
    <remove-beneficiary
      v-if="showRemoveBeneficiary"
      :showDialog="showRemoveBeneficiary"
      @closeDialog="closeRemoveBeneficiary"
      @removeBeneficiary="removeBeneficiary"
    />
  </main>
</template>

<script>
import bulkPayout from '@/mixins/bulkPayout.js'
import { ParseAmount } from '@/utils/helpers.js'

export default {
  name: 'SelectMultiBeneficiary',
  mixins: [bulkPayout],
  components: {
    BulkPayoutIcon2: () => import('@/assets/v2/icons/BulkPayoutIcon2.vue'),
    EnterAmount: () => import('@/components/v2/bulkPayout/dialogs/EnterAmount'),
    RemoveBeneficiary: () =>
      import('@/components/v2/bulkPayout/dialogs/RemoveBeneficiary'),
    AddBeneficiary: () =>
      import('@/components/v2/bulkPayout/drawers/AddBeneficiaryDrawer'),
    EditBeneficiary: () =>
      import('@/components/v2/bulkPayout/drawers/EditBeneficiary'),
    EmptyIcon: () => import('@/assets/v2/icons/EmptyState2.vue'),
    PlusIcon: () => import('@/assets/v2/icons/AddIcon.vue'),
    MoreIcon: () => import('@/assets/icons/MoreIcon.vue'),
    ArrowIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    EditIcon: () => import('@/assets/v2/icons/EditIcon2.vue'),
    RemoveIcon: () => import('@/assets/v2/icons/RemoveIcon.vue'),
    Spinner: () => import('@/components/v2/app/Spinner.vue')
  },
  data () {
    return {
      search: '',
      selectedId: '',
      details: {},
      isLoading: false,
      showAmountDialog: false,
      showReviewOptions: false,
      showRemoveBeneficiary: false,
      showAddBeneficiary: false,
      showEditBeneficiary: false
    }
  },
  computed: {
    isMno () {
      return (
        this.$store.state.bulkPayout.bulkPayoutForm?.paymentMethod === 'mno'
      )
    },
    selectedBeneficiaries () {
      return this.$store.state.bulkPayout.selectedBeneficiaries || []
    },
    filteredBeneficiaries () {
      const isMno = this.isMno
      return this.filterSavedBeneficiaries.filter((beneficiary) => {
        const isMobileMoney = beneficiary.details?.bankDetails?.isMobileMoney
        if (isMno) {
          return isMobileMoney === 'yes' || isMobileMoney === 'true'
        } else {
          return this.filterSavedBeneficiaries
        }
      })
    }
  },
  methods: {
    goBack () {
      this.$router.push('.')
    },
    showDialog (item) {
      this.details = item
      this.showAmountDialog = true
    },
    closeDialog () {
      this.showAmountDialog = false
    },
    openReviewOptions (id) {
      this.selectedId = id
      this.showReviewOptions = true
    },
    closeReviewOptions () {
      this.showReviewOptions = false
    },
    openRemoveBeneficiary () {
      this.showRemoveBeneficiary = true
    },
    closeRemoveBeneficiary () {
      this.showRemoveBeneficiary = false
    },
    openAddBeneficiary () {
      this.showAddBeneficiary = true
    },
    closeAddBeneficiary () {
      this.showAddBeneficiary = false
      this.getPayoutBeneficiaries(this.currency)
    },
    openEditBeneficiary (item) {
      this.details = item
      this.showEditBeneficiary = true
      this.showReviewOptions = false
    },
    closeEditBeneficiary () {
      this.showEditBeneficiary = false
    },
    parseAmount (val) {
      return ParseAmount(val)
    },
    removeBeneficiary () {
      const filteredBeneficiary = this.selectedBeneficiaries.filter(
        (item) => item.id !== this.selectedId
      )
      this.showRemoveBeneficiary = false
      this.$store.dispatch(
        'bulkPayout/removeSelectedBeneficiaries',
        filteredBeneficiary
      )
      this.showReviewOptions = false
    },
    confirmBeneficiaries () {
      this.$router.push('.')
    }
  },
  mounted () {
    this.getPayoutBeneficiaries(this.currency)
  }
}
</script>

<style>
.review-items {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  padding: 25px 0;
  border-bottom: 1px solid #e1e3f2;

  &:last-child {
    border-bottom: 0;
  }
}

.list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  cursor: pointer;
  color: #171717;

  &:hover {
    color: var(--color-primary);
  }

  &:hover #icon path {
    fill: var(--color-primary);
  }
}

>>> .el-input--prefix .el-input__inner {
  padding-left: 40px !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
